import * as yup from "yup";
import type { LoginUserInput } from "@/types";
import Form from "@/components/ui/forms/Form.tsx";
import routes from "@/config/routes";
import LockIcon from "@/components/icons/LockIcon.tsx";
import Link from "@/components/ui/links/Link.tsx";
import { useLoginMutation } from "@/data/user.ts";
import useAuth from "@/utils/hooks/useAuth.ts";
import OutlinedInput from "@edusogno/inputs/outlined-input.js";
import OutlinedPasswordInput from "@edusogno/inputs/outlined-password-input.js";
import AuthBar from "@edusogno/layouts/auth-bar.js";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer.js";
import AuthPageHeading from "@edusogno/layouts/auth-page-heading.js";
import { cn } from "@edusogno/lib/utils";
import LoadingButton from "@edusogno/primitives/loading-button";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import type { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function LoginUserForm({
  redirect = true,
}: {
  redirect?: boolean;
}) {
  const { t } = useTranslation();

  const loginValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("auth.errors.valid_email"))
      .required(t("auth.errors.email_required")),
    password: yup.string().required(t("auth.errors.password_required")),
  });
  const { authorize } = useAuth();
  const navigate = useNavigate();
  const { mutate: login, isLoading } = useLoginMutation();

  const onSubmit: SubmitHandler<LoginUserInput> = (data) => {
    login(data, {
      onSuccess: (response) => {
        if (response.status !== 200) {
          enqueueSnackbar("Something went wrong. please retry", {
            variant: "error",
          });
          return;
        }

        if (!response.item.user.created_account) {
          enqueueSnackbar("Complete your registration", {
            variant: "warning",
            action: (key) => (
              <Link
                to={routes.register.replace(":token", response.item.user.token)}
                onClick={() => closeSnackbar(key)}
                className={
                  "text-brand font-semibold bg-white cursor-pointer px-2 py-1 rounded-md"
                }
              >
                Click here
              </Link>
            ),
          });
          return;
        }

        enqueueSnackbar("You have successfully logged in", {
          variant: "success",
        });
        authorize(response.item.token);
        if (redirect) {
          navigate(routes.home);
        }
      },
    });
  };
  return (
    <Form<LoginUserInput>
      onSubmit={onSubmit}
      className={"flex flex-col h-full flex-[2_3_100%] items-center"}
      validationSchema={loginValidationSchema}
      id={"login-form"}
    >
      {({ register, formState: { errors } }) => (
        <>
          <AuthBar progress={0} onClick={() => {}} />

          <div
            className={
              "flex flex-col flex-[4_0_40%]  lg:max-w-[55%] lg:min-w-[500px]  pt-6 2xl:pt-8  mx-auto gap-3  px-5 md:gap-4  w-full items-center "
            }
          >
            <AuthPageHeading
              title={t("auth.login.text")}
              subtitle={t("auth.login.subtext")}
            />
            <div
              className={cn(
                "flex flex-col flex-[1_1_100%]  3xl:pt-12 w-full gap-4 h-full",
              )}
            >
              <OutlinedInput
                label={t("auth.labels.email")}
                placeholder={"hello@you.com"}
                autoComplete={"off"}
                size={"lg"}
                color={"grey"}
                type="email"
                {...register("email")}
                error={Boolean(errors.email?.message)}
              />
              <OutlinedPasswordInput
                label={t("auth.labels.password")}
                autoComplete={"off"}
                placeholder={"********"}
                {...register("password")}
                size={"lg"}
                color={"grey"}
                error={Boolean(errors.password?.message)}
              />
            </div>
          </div>

          <AuthPageFooter>
            <div
              className={
                "font-normal text-lg flex flex-row items-center justify-center md:text-lg  pt-4 mb-3 text-center text-brand w-full px-1"
              }
            >
              <Link to={routes.reset_password}>
                <div
                  className={
                    "flex flex-row  lg:mb-0 items-center justify-end gap-2"
                  }
                >
                  <LockIcon className={"w-5 h-5"} />
                  {t("auth.password.forgot")}
                </div>
              </Link>
            </div>
            <div
              className={
                "w-full max-w-md mx-auto flex flex-col items-center px-6 mb-4"
              }
            >
              <LoadingButton
                size={"lg"}
                type="submit"
                className="w-full text-lg tracking-wide hidden md:block"
                loading={isLoading}
                disabled={isLoading}
              >
                {t("auth.login.login")}
              </LoadingButton>
              <LoadingButton
                size={"md"}
                type="submit"
                className="w-full text-lg tracking-wide block md:hidden"
                loading={isLoading}
                disabled={isLoading}
              >
                {t("auth.login.login")}
              </LoadingButton>
            </div>
          </AuthPageFooter>
        </>
      )}
    </Form>
  );
}
